import { useEffect, useMemo, useState } from "react";
import useSWR from "swr";
import { fetchLeagues, fetchMatches, romeDate } from "../lib/api";
import Sidebar from "../components/Sidebar";
import { AccountBox } from "../components/Account";
import MatchCard from "../components/MatchCard";
import { Search, Loader2, Satellite, SlidersHorizontal } from "lucide-react";

const DAYS = [
  { offset: -1, label: "Ieri" },
  { offset: 0, label: "Oggi" },
  { offset: 1, label: "Domani" },
  { offset: 2 },
  { offset: 3 },
];

function dayLabel(d) {
  if (d.label) return d.label;
  return new Date(Date.now() + d.offset * 86400000).toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", timeZone: "Europe/Rome" });
}

export default function Dashboard() {
  const [league, setLeague] = useState(null);
  const [status, setStatus] = useState(null);
  const [offset, setOffset] = useState(0);
  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const date = romeDate(offset);

  const { data: leagues = [] } = useSWR("leagues", fetchLeagues);
  const { data: allMatches = [], isLoading, error } = useSWR(["matches", date], () => fetchMatches({ date }), {
    refreshInterval: 30000,
  });

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allMatches.filter(
      (m) =>
        (!league || m.league_id === league) &&
        (!status || m.status === status) &&
        (!q || m.home.name.toLowerCase().includes(q) || m.away.name.toLowerCase().includes(q))
    );
  }, [allMatches, league, status, query]);

  const counts = useMemo(() => {
    const byLeague = {};
    allMatches.forEach((m) => (byLeague[m.league_id] = (byLeague[m.league_id] || 0) + 1));
    return { live: allMatches.filter((m) => m.status === "live").length, byLeague };
  }, [allMatches]);

  const groups = useMemo(() => {
    const g = new Map();
    filtered.forEach((m) => {
      if (!g.has(m.league_id)) g.set(m.league_id, { league: m.league, matches: [] });
      g.get(m.league_id).matches.push(m);
    });
    const order = leagues.map((l) => l.id);
    return [...g.entries()].sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0])).map(([, v]) => v);
  }, [filtered, leagues]);

  useEffect(() => {
    document.title = "GOAL.AI — Pronostici Calcio con AI";
  }, []);

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      <div className={`${filtersOpen ? "block" : "hidden"} lg:block`}>
        <Sidebar
          leagues={leagues}
          activeLeague={league}
          onLeague={(v) => { setLeague(v); setFiltersOpen(false); }}
          activeStatus={status}
          onStatus={(v) => { setStatus(v); setFiltersOpen(false); }}
          counts={counts}
        />
      </div>

      <main className="flex-1 min-w-0">
        <header className="sticky top-0 z-20 glass border-b border-[#27272A]">
          <div className="px-6 lg:px-10 py-5 flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
            <div>
              <h1 className="heading text-3xl lg:text-4xl font-black tracking-tighter">
                Pronostici Calcio <span className="text-[#39FF14]">AI</span>
              </h1>
              <p className="text-sm text-[#A1A1AA] mt-1">Partite reali, formazioni, quote e meteo aggiornati in tempo reale.</p>
            </div>
            <div className="flex items-center gap-2">
            <button
              onClick={() => setFiltersOpen((o) => !o)}
              data-testid="mobile-filters-toggle"
              className="lg:hidden flex items-center gap-2 px-3 py-2.5 border border-[#27272A] text-sm text-[#A1A1AA] hover:text-white transition-colors"
            >
              <SlidersHorizontal size={14} strokeWidth={1.5} /> Filtri{league || status ? " •" : ""}
            </button>
            <div className="relative flex-1">
              <Search size={14} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
              <input
                data-testid="search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Cerca squadra…"
                className="bg-[#121212] border border-[#27272A] pl-9 pr-4 py-2.5 text-sm w-full lg:w-64 focus:outline-none focus:border-[#39FF14] transition-colors"
              />
            </div>
            </div>
          </div>

          {!filtersOpen && <div className="lg:hidden px-6 pb-4" data-testid="mobile-account"><AccountBox /></div>}
          <div className="px-6 lg:px-10 pb-4 flex flex-wrap items-center gap-2">
            {DAYS.map((d) => (
              <button
                key={d.offset}
                data-testid={`day-tab-${d.offset}`}
                onClick={() => setOffset(d.offset)}
                className={`px-4 py-1.5 text-xs font-bold uppercase tracking-wider border transition-colors ${
                  offset === d.offset ? "bg-[#39FF14] text-black border-[#39FF14]" : "border-[#27272A] text-[#A1A1AA] hover:text-white hover:border-[#3f3f46]"
                }`}
              >
                {dayLabel(d)}
              </button>
            ))}
            <div className="ml-auto flex items-center gap-4 text-[#A1A1AA]">
              <span className="mono text-[11px]" data-testid="matches-count">{filtered.length} partite</span>
              <span className="hidden md:flex items-center gap-2">
                <Satellite size={12} strokeWidth={1.5} />
                <span className="mono text-[10px]">Live ogni 30s</span>
              </span>
            </div>
          </div>
        </header>

        <section className="px-6 lg:px-10 py-8">
          {isLoading && (
            <div className="flex items-center gap-3 text-[#A1A1AA]">
              <Loader2 size={18} className="animate-spin" /> Caricamento partite reali…
            </div>
          )}
          {error && <div className="text-[#FF3B30] text-sm">Errore nel caricamento delle partite. Riprova tra poco.</div>}
          {!isLoading && !error && filtered.length === 0 && (
            <div className="border border-dashed border-[#27272A] p-10 text-center" data-testid="empty-state">
              <div className="label mb-2">Nessuna partita</div>
              <p className="text-sm text-[#A1A1AA]">Nessuna partita per questo giorno/filtro. Prova un altro giorno o campionato.</p>
            </div>
          )}

          <div className="columns-1 md:columns-2 2xl:columns-3 gap-6" data-testid="matches-grid">
          {groups.map((g) => (
            <div key={g.league.id || g.league.name} className="break-inside-avoid mb-8" data-testid={`league-group-${g.league.code}`}>
              <div className="flex items-baseline gap-3 mb-4">
                <h2 className="heading text-xl font-black">{g.league.name}</h2>
                <span className="label text-[10px]">{g.league.country}</span>
                <span className="mono text-[10px] text-[#71717A]">{g.matches.length}</span>
              </div>
              <div className="space-y-3">
                {g.matches.map((m, i) => <MatchCard key={m.id} match={m} index={i} />)}
              </div>
            </div>
          ))}
          </div>
        </section>
      </main>
    </div>
  );
}
